// pages/LearningResultsPage.js
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  AppBar,
  Toolbar,
  Button,
  Grid,
  Card,
  Avatar,
  IconButton,
  Tab,
  Tabs,
  Paper,
  LinearProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import {
  School,
  ExitToApp,
  Home,
  TrendingUp,
  Assessment,
  EmojiEvents,
  CheckCircle,
  Schedule,
  Star,
} from "@mui/icons-material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { useAuth } from "../contexts/AuthContext";
import { getAnimalAvatar, getDisplayUsername } from "../utils/userUtils";

// Academic Theme
const academicTheme = createTheme({
  palette: {
    primary: {
      main: "#1565C0",
    },
    secondary: {
      main: "#FF8F00",
    },
    background: {
      default: "#F8F9FA",
      paper: "#FFFFFF",
    },
  },
});

// Mock learning results data
const learningData = {
  overview: {
    totalCourses: 8,
    completedCourses: 6,
    inProgressCourses: 2,
    totalHours: 145,
    averageScore: 87.5,
    rank: 12,
    totalStudents: 247,
  },
  courses: [
    {
      id: 1,
      title: "การวิเคราะห์ข้อมูลขั้นสูง",
      instructor: "ดร.สมชาย วิเคราะห์",
      progress: 100,
      score: 92,
      grade: "A",
      status: "completed",
      completedDate: "2024-07-15",
      studyHours: 24,
    },
    {
      id: 2,
      title: "Machine Learning ในทางปฏิบัติ",
      instructor: "Prof. John Smith",
      progress: 100,
      score: 88,
      grade: "B+",
      status: "completed",
      completedDate: "2024-06-20",
      studyHours: 32,
    },
    {
      id: 3,
      title: "ความปลอดภัยไซเบอร์",
      instructor: "อ.ปรีชา เซเคียวริตี้",
      progress: 100,
      score: 85,
      grade: "B+",
      status: "completed",
      completedDate: "2024-05-10",
      studyHours: 18,
    },
    {
      id: 4,
      title: "การพัฒนาเว็บแอปพลิเคชัน",
      instructor: "อ.ธนาธิป โปรแกรม",
      progress: 100,
      score: 90,
      grade: "A",
      status: "completed",
      completedDate: "2024-04-25",
      studyHours: 28,
    },
    {
      id: 5,
      title: "Internet of Things (IoT)",
      instructor: "ศ.ดร.วิทยา อินเทอร์เน็ต",
      progress: 75,
      score: 82,
      grade: "B",
      status: "in-progress",
      studyHours: 21,
    },
    {
      id: 6,
      title: "Blockchain และคริปโตเคอเรนซี",
      instructor: "หน่วยวิจัยฟินเทค",
      progress: 45,
      score: 0,
      grade: "-",
      status: "in-progress",
      studyHours: 12,
    },
  ],
  achievements: [
    {
      id: 1,
      title: "นักเรียนดีเด่น",
      description: "ผลการเรียนเฉลี่ย 85% ขึ้นไป",
      icon: "🏆",
      earned: true,
      earnedDate: "2024-07-01",
    },
    {
      id: 2,
      title: "นักเรียนขยัน",
      description: "เรียนครบ 100 ชั่วโมง",
      icon: "📚",
      earned: true,
      earnedDate: "2024-06-15",
    },
    {
      id: 3,
      title: "เซียนเทคโนโลยี",
      description: "จบคอร์สเทคโนโลยี 5 คอร์ส",
      icon: "💻",
      earned: true,
      earnedDate: "2024-05-20",
    },
    {
      id: 4,
      title: "นักวิจัยมือใหม่",
      description: "อ่านงานวิจัย 20 เรื่อง",
      icon: "🔬",
      earned: false,
    },
  ],
};

const LearningResultsPage = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [selectedTab, setSelectedTab] = useState(0);

  const username = user?.username || "ผู้ใช้ระบบ";
  const displayName = getDisplayUsername(username, "short_student_id");
  const animalAvatar = getAnimalAvatar(username);

  const handleLogout = () => {
    logout();
    navigate("/academic");
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "completed":
        return "#2E7D32";
      case "in-progress":
        return "#FF8F00";
      default:
        return "#757575";
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case "completed":
        return "จบแล้ว";
      case "in-progress":
        return "กำลังเรียน";
      default:
        return "ยังไม่เริ่ม";
    }
  };

  const getGradeColor = (grade) => {
    if (grade === "A") return "#2E7D32";
    if (grade === "B+" || grade === "B") return "#FF8F00";
    if (grade === "C+" || grade === "C") return "#FB8C00";
    if (grade === "D+" || grade === "D") return "#F57C00";
    return "#757575";
  };

  return (
    <ThemeProvider theme={academicTheme}>
      <Box sx={{ minHeight: "100vh", backgroundColor: "#F8F9FA" }}>
        {/* Navigation Bar */}
        <AppBar position="static" sx={{ backgroundColor: "#1565C0" }}>
          <Toolbar>
            <Box sx={{ display: "flex", alignItems: "center", flexGrow: 1 }}>
              <IconButton
                onClick={() => navigate("/dashboard")}
                sx={{ color: "white", mr: 2 }}
              >
                <School sx={{ fontSize: 32 }} />
              </IconButton>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                ผลการเรียน
              </Typography>
            </Box>

            <Button
              startIcon={<Home />}
              sx={{ color: "white", mr: 2 }}
              onClick={() => navigate("/dashboard")}
            >
              หน้าหลัก
            </Button>

            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Avatar sx={{ bgcolor: "#FF8F00", mr: 1 }}>{animalAvatar}</Avatar>
              <Typography variant="body2" sx={{ color: "white", mr: 2 }}>
                {displayName}
              </Typography>
              <IconButton onClick={handleLogout} sx={{ color: "white" }}>
                <ExitToApp />
              </IconButton>
            </Box>
          </Toolbar>
        </AppBar>

        <Container maxWidth="lg" sx={{ py: 4 }}>
          {/* Header */}
          <Box sx={{ mb: 4, textAlign: "center" }}>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 700,
                color: "#1565C0",
                mb: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Assessment sx={{ fontSize: 48, mr: 2 }} />
              ผลการเรียนของฉัน
            </Typography>
            <Typography variant="h6" sx={{ color: "#666", mb: 4 }}>
              ติดตามความก้าวหน้าในการเรียนรู้
            </Typography>
          </Box>

          {/* Overview Cards */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card
                sx={{
                  p: 3,
                  textAlign: "center",
                  borderRadius: 3,
                  background:
                    "linear-gradient(135deg, #1565C0 0%, #42A5F5 100%)",
                  color: "white",
                }}
              >
                <TrendingUp sx={{ fontSize: 48, mb: 1 }} />
                <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                  {learningData.overview.averageScore}%
                </Typography>
                <Typography variant="body2">คะแนนเฉลี่ย</Typography>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card
                sx={{
                  p: 3,
                  textAlign: "center",
                  borderRadius: 3,
                  background:
                    "linear-gradient(135deg, #2E7D32 0%, #66BB6A 100%)",
                  color: "white",
                }}
              >
                <CheckCircle sx={{ fontSize: 48, mb: 1 }} />
                <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                  {learningData.overview.completedCourses}
                </Typography>
                <Typography variant="body2">คอร์สที่จบแล้ว</Typography>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card
                sx={{
                  p: 3,
                  textAlign: "center",
                  borderRadius: 3,
                  background:
                    "linear-gradient(135deg, #FF8F00 0%, #FFB74D 100%)",
                  color: "white",
                }}
              >
                <Schedule sx={{ fontSize: 48, mb: 1 }} />
                <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                  {learningData.overview.totalHours}
                </Typography>
                <Typography variant="body2">ชั่วโมงการเรียน</Typography>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card
                sx={{
                  p: 3,
                  textAlign: "center",
                  borderRadius: 3,
                  background:
                    "linear-gradient(135deg, #7B1FA2 0%, #BA68C8 100%)",
                  color: "white",
                }}
              >
                <EmojiEvents sx={{ fontSize: 48, mb: 1 }} />
                <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                  #{learningData.overview.rank}
                </Typography>
                <Typography variant="body2">
                  อันดับใน {learningData.overview.totalStudents} คน
                </Typography>
              </Card>
            </Grid>
          </Grid>

          {/* Tabs */}
          <Box sx={{ mb: 4 }}>
            <Tabs
              value={selectedTab}
              onChange={(e, newValue) => setSelectedTab(newValue)}
              centered
              sx={{
                "& .MuiTab-root": {
                  fontWeight: 600,
                  fontSize: "1rem",
                },
              }}
            >
              <Tab label="รายละเอียดคอร์ส" />
              <Tab label="รางวัลและผลงาน" />
            </Tabs>
          </Box>

          {/* Tab Content */}
          {selectedTab === 0 && (
            <TableContainer component={Paper} sx={{ borderRadius: 3 }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#F5F5F5" }}>
                    <TableCell sx={{ fontWeight: 600 }}>คอร์สเรียน</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>อาจารย์</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>ความคืบหน้า</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>คะแนน</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>เกรด</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>สถานะ</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>ชั่วโมง</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {learningData.courses.map((course) => (
                    <TableRow key={course.id}>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 600, color: "#1565C0" }}
                        >
                          {course.title}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {course.instructor}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ minWidth: 100 }}>
                          <LinearProgress
                            variant="determinate"
                            value={course.progress}
                            sx={{
                              height: 8,
                              borderRadius: 4,
                              backgroundColor: "#E0E0E0",
                              "& .MuiLinearProgress-bar": {
                                backgroundColor:
                                  course.progress === 100
                                    ? "#2E7D32"
                                    : "#FF8F00",
                              },
                            }}
                          />
                          <Typography
                            variant="caption"
                            sx={{ color: "#666", mt: 0.5 }}
                          >
                            {course.progress}%
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {course.score > 0 ? course.score : "-"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={course.grade}
                          size="small"
                          sx={{
                            backgroundColor: getGradeColor(course.grade),
                            color: "white",
                            fontWeight: 600,
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getStatusLabel(course.status)}
                          size="small"
                          sx={{
                            backgroundColor: getStatusColor(course.status),
                            color: "white",
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {course.studyHours} ชม.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {selectedTab === 1 && (
            <Grid container spacing={3}>
              {learningData.achievements.map((achievement) => (
                <Grid item xs={12} sm={6} md={4} key={achievement.id}>
                  <Card
                    sx={{
                      p: 3,
                      borderRadius: 3,
                      textAlign: "center",
                      opacity: achievement.earned ? 1 : 0.5,
                      border: achievement.earned
                        ? "2px solid #FFD700"
                        : "2px solid #E0E0E0",
                      background: achievement.earned
                        ? "linear-gradient(135deg, #FFF8E1 0%, #FFECB3 100%)"
                        : "#F5F5F5",
                    }}
                  >
                    <Typography sx={{ fontSize: 48, mb: 2 }}>
                      {achievement.earned ? achievement.icon : "🔒"}
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        mb: 1,
                        color: achievement.earned ? "#FF8F00" : "#666",
                      }}
                    >
                      {achievement.title}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#666", mb: 2 }}>
                      {achievement.description}
                    </Typography>
                    {achievement.earned && (
                      <Chip
                        icon={<Star />}
                        label={`ได้รับเมื่อ ${achievement.earnedDate}`}
                        size="small"
                        sx={{
                          backgroundColor: "#FFD700",
                          color: "#FF8F00",
                          fontWeight: 600,
                        }}
                      />
                    )}
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Container>
      </Box>
    </ThemeProvider>
  );
};

export default LearningResultsPage;
